import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiPlus, FiX, FiCopy, FiSave, FiSearch, FiChevronDown,
  FiShoppingCart, FiCalendar, FiUsers, FiFilter
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { searchFoods } from '../../services/database';
import { useTrainerClients } from '../../hooks/useFirestore';
import './MealPlanner.css';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MEAL_TYPES = ['Breakfast', 'Lunch', 'Dinner', 'Snack 1', 'Snack 2'];

const MealPlanner = () => {
  const { currentUser } = useAuth();
  const { data: clients } = useTrainerClients(currentUser?.uid);

  // State
  const [selectedClient, setSelectedClient] = useState(null);
  const [mealPlan, setMealPlan] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [draggedFood, setDraggedFood] = useState(null);
  const [copiedMeal, setCopiedMeal] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);

  // Initialize empty meal plan
  useEffect(() => {
    const initialPlan = {};
    DAYS_OF_WEEK.forEach(day => {
      initialPlan[day] = {};
      MEAL_TYPES.forEach(meal => {
        initialPlan[day][meal] = [];
      });
    });
    setMealPlan(initialPlan);
  }, []);

  // Search foods
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchFoods(query, 20);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching foods:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // Drag and drop handlers
  const handleDragStart = (e, food) => {
    setDraggedFood(food);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e, day, mealType) => {
    e.preventDefault();

    if (!draggedFood) return;

    const foodItem = {
      ...draggedFood,
      id: `${Date.now()}-${Math.random()}`,
      servings: 1,
      totalCalories: draggedFood.calories,
      totalProtein: draggedFood.protein,
      totalCarbs: draggedFood.carbs,
      totalFat: draggedFood.fat
    };

    setMealPlan(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [mealType]: [...prev[day][mealType], foodItem]
      }
    }));

    setDraggedFood(null);
  };

  // Update servings
  const updateServings = (day, mealType, foodId, servings) => {
    setMealPlan(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [mealType]: prev[day][mealType].map(food =>
          food.id === foodId
            ? {
                ...food,
                servings: parseFloat(servings) || 1,
                totalCalories: food.calories * (parseFloat(servings) || 1),
                totalProtein: food.protein * (parseFloat(servings) || 1),
                totalCarbs: food.carbs * (parseFloat(servings) || 1),
                totalFat: food.fat * (parseFloat(servings) || 1)
              }
            : food
        )
      }
    }));
  };

  // Remove food
  const removeFood = (day, mealType, foodId) => {
    setMealPlan(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [mealType]: prev[day][mealType].filter(food => food.id !== foodId)
      }
    }));
  };

  // Copy meal
  const copyMeal = (day, mealType) => {
    setCopiedMeal({
      foods: mealPlan[day][mealType],
      sourceMeal: mealType
    });
  };

  // Paste meal
  const pasteMeal = (day, mealType) => {
    if (!copiedMeal) return;

    const newFoods = copiedMeal.foods.map(food => ({
      ...food,
      id: `${Date.now()}-${Math.random()}`
    }));

    setMealPlan(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [mealType]: [...prev[day][mealType], ...newFoods]
      }
    }));
  };

  // Copy entire day
  const copyDay = (sourceDay, targetDay) => {
    setMealPlan(prev => ({
      ...prev,
      [targetDay]: JSON.parse(JSON.stringify(prev[sourceDay]))
    }));
  };

  // Calculate daily totals
  const calculateDailyTotals = (day) => {
    let totals = { calories: 0, protein: 0, carbs: 0, fat: 0 };

    MEAL_TYPES.forEach(mealType => {
      mealPlan[day]?.[mealType]?.forEach(food => {
        totals.calories += food.totalCalories || 0;
        totals.protein += food.totalProtein || 0;
        totals.carbs += food.totalCarbs || 0;
        totals.fat += food.totalFat || 0;
      });
    });

    return totals;
  };

  // Calculate meal totals
  const calculateMealTotals = (day, mealType) => {
    let totals = { calories: 0, protein: 0, carbs: 0, fat: 0 };

    mealPlan[day]?.[mealType]?.forEach(food => {
      totals.calories += food.totalCalories || 0;
      totals.protein += food.totalProtein || 0;
      totals.carbs += food.totalCarbs || 0;
      totals.fat += food.totalFat || 0;
    });

    return totals;
  };

  // Calculate weekly totals
  const weeklyTotals = useMemo(() => {
    let totals = { calories: 0, protein: 0, carbs: 0, fat: 0 };

    DAYS_OF_WEEK.forEach(day => {
      const dayTotals = calculateDailyTotals(day);
      totals.calories += dayTotals.calories;
      totals.protein += dayTotals.protein;
      totals.carbs += dayTotals.carbs;
      totals.fat += dayTotals.fat;
    });

    return {
      calories: Math.round(totals.calories),
      protein: Math.round(totals.protein),
      carbs: Math.round(totals.carbs),
      fat: Math.round(totals.fat),
      avgDaily: {
        calories: Math.round(totals.calories / 7),
        protein: Math.round(totals.protein / 7),
        carbs: Math.round(totals.carbs / 7),
        fat: Math.round(totals.fat / 7)
      }
    };
  }, [mealPlan]);

  // Save meal plan
  const saveMealPlan = async () => {
    if (!selectedClient) {
      alert('Please select a client first');
      return;
    }

    try {
      const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../firebase/config');

      const mealPlanData = {
        clientId: selectedClient.uid,
        clientName: selectedClient.displayName,
        trainerId: currentUser.uid,
        weekStartDate: new Date(),
        meals: mealPlan,
        weeklyTotals: weeklyTotals,
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'mealPlans'), mealPlanData);
      console.log('Meal plan saved successfully for client:', selectedClient.displayName);
      alert('Meal plan saved successfully!');
    } catch (error) {
      console.error('Error saving meal plan:', error);
      alert('Error saving meal plan. Please try again.');
    }
  };

  // Generate shopping list
  const generateShoppingList = () => {
    const ingredients = {};

    DAYS_OF_WEEK.forEach(day => {
      MEAL_TYPES.forEach(mealType => {
        mealPlan[day]?.[mealType]?.forEach(food => {
          const key = food.name;
          if (ingredients[key]) {
            ingredients[key].quantity += food.servings;
          } else {
            ingredients[key] = {
              name: food.name,
              quantity: food.servings,
              unit: food.servingUnit || 'serving'
            };
          }
        });
      });
    });

    return Object.values(ingredients);
  };

  return (
    <div className="meal-planner">
      {/* Header */}
      <div className="planner-header">
        <div className="header-left">
          <h1>Weekly Meal Planner</h1>
          <p>Create and assign meal plans to your clients</p>
        </div>

        <div className="header-actions">
          {/* Client Selector */}
          <select
            value={selectedClient?.uid || ''}
            onChange={(e) => setSelectedClient(clients.find(c => c.uid === e.target.value))}
            className="client-select"
          >
            <option value="">Select Client</option>
            {clients.map(client => (
              <option key={client.uid} value={client.uid}>
                {client.displayName}
              </option>
            ))}
          </select>

          <button className="btn-secondary" onClick={() => setShowTemplates(!showTemplates)}>
            <FiFilter />
            Templates
          </button>

          <button className="btn-secondary" onClick={() => {
            const list = generateShoppingList();
            console.log('Shopping list:', list);
            // TODO: Show shopping list modal
          }}>
            <FiShoppingCart />
            Shopping List
          </button>

          <button className="btn-primary" onClick={saveMealPlan} disabled={!selectedClient}>
            <FiSave />
            Save Plan
          </button>
        </div>
      </div>

      {/* Weekly Summary */}
      <div className="weekly-summary">
        <div className="summary-card">
          <label>Weekly Totals</label>
          <div className="totals-grid">
            <div className="total-item">
              <span className="value">{weeklyTotals.calories}</span>
              <span className="label">Calories</span>
            </div>
            <div className="total-item">
              <span className="value">{weeklyTotals.protein}g</span>
              <span className="label">Protein</span>
            </div>
            <div className="total-item">
              <span className="value">{weeklyTotals.carbs}g</span>
              <span className="label">Carbs</span>
            </div>
            <div className="total-item">
              <span className="value">{weeklyTotals.fat}g</span>
              <span className="label">Fat</span>
            </div>
          </div>
        </div>

        <div className="summary-card">
          <label>Daily Average</label>
          <div className="totals-grid">
            <div className="total-item">
              <span className="value">{weeklyTotals.avgDaily.calories}</span>
              <span className="label">Calories</span>
            </div>
            <div className="total-item">
              <span className="value">{weeklyTotals.avgDaily.protein}g</span>
              <span className="label">Protein</span>
            </div>
            <div className="total-item">
              <span className="value">{weeklyTotals.avgDaily.carbs}g</span>
              <span className="label">Carbs</span>
            </div>
            <div className="total-item">
              <span className="value">{weeklyTotals.avgDaily.fat}g</span>
              <span className="label">Fat</span>
            </div>
          </div>
        </div>
      </div>

      <div className="planner-content">
        {/* Food Search Panel */}
        <div className="food-search-panel">
          <div className="search-header">
            <h3>Food Database</h3>
            <FiSearch />
          </div>

          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search foods..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="search-results">
            {isSearching && <div className="searching">Searching...</div>}

            {!isSearching && searchResults.length === 0 && searchQuery && (
              <div className="no-results">No foods found</div>
            )}

            {!isSearching && searchResults.length === 0 && !searchQuery && (
              <div className="search-prompt">
                <FiSearch size={40} />
                <p>Search for foods to add to your meal plan</p>
              </div>
            )}

            {!isSearching && searchResults.map(food => (
              <div
                key={food.id}
                className="food-item"
                draggable
                onDragStart={(e) => handleDragStart(e, food)}
              >
                <div className="food-info">
                  <strong>{food.name}</strong>
                  {food.brand && <span className="brand">{food.brand}</span>}
                  <div className="food-macros">
                    <span>{Math.round(food.calories)}cal</span>
                    <span>P: {Math.round(food.protein)}g</span>
                    <span>C: {Math.round(food.carbs)}g</span>
                    <span>F: {Math.round(food.fat)}g</span>
                  </div>
                </div>
                <div className="drag-indicator">⋮⋮</div>
              </div>
            ))}
          </div>
        </div>

        {/* Meal Plan Grid */}
        <div className="meal-plan-grid">
          {DAYS_OF_WEEK.map(day => {
            const dailyTotals = calculateDailyTotals(day);

            return (
              <div key={day} className="day-column">
                {/* Day Header */}
                <div className="day-header">
                  <div className="day-info">
                    <h3>{day}</h3>
                    <div className="day-totals">
                      <span>{Math.round(dailyTotals.calories)} cal</span>
                      <span>P: {Math.round(dailyTotals.protein)}g</span>
                      <span>C: {Math.round(dailyTotals.carbs)}g</span>
                      <span>F: {Math.round(dailyTotals.fat)}g</span>
                    </div>
                  </div>

                  <div className="day-actions">
                    <button
                      className="icon-btn"
                      title="Copy day"
                      onClick={() => {
                        const targetDay = prompt(`Copy ${day} to which day?`);
                        if (targetDay && DAYS_OF_WEEK.includes(targetDay)) {
                          copyDay(day, targetDay);
                        }
                      }}
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>

                {/* Meals */}
                {MEAL_TYPES.map(mealType => {
                  const mealTotals = calculateMealTotals(day, mealType);
                  const foods = mealPlan[day]?.[mealType] || [];

                  return (
                    <div
                      key={mealType}
                      className="meal-slot"
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, day, mealType)}
                    >
                      <div className="meal-header">
                        <h4>{mealType}</h4>
                        <div className="meal-actions">
                          <button
                            className="icon-btn-sm"
                            title="Copy meal"
                            onClick={() => copyMeal(day, mealType)}
                          >
                            <FiCopy />
                          </button>
                          {copiedMeal && (
                            <button
                              className="icon-btn-sm"
                              title="Paste meal"
                              onClick={() => pasteMeal(day, mealType)}
                            >
                              📋
                            </button>
                          )}
                        </div>
                      </div>

                      {foods.length === 0 ? (
                        <div className="empty-meal">
                          <p>Drag foods here</p>
                        </div>
                      ) : (
                        <>
                          {foods.map(food => (
                            <div key={food.id} className="meal-food-item">
                              <div className="food-details">
                                <strong>{food.name}</strong>
                                <div className="food-nutrition">
                                  <span>{Math.round(food.totalCalories)}cal</span>
                                  <span>P:{Math.round(food.totalProtein)}</span>
                                  <span>C:{Math.round(food.totalCarbs)}</span>
                                  <span>F:{Math.round(food.totalFat)}</span>
                                </div>
                              </div>

                              <div className="food-controls">
                                <input
                                  type="number"
                                  min="0.1"
                                  step="0.1"
                                  value={food.servings}
                                  onChange={(e) =>
                                    updateServings(day, mealType, food.id, e.target.value)
                                  }
                                  className="servings-input"
                                />
                                <span className="servings-label">x</span>
                                <button
                                  className="remove-btn"
                                  onClick={() => removeFood(day, mealType, food.id)}
                                >
                                  <FiX />
                                </button>
                              </div>
                            </div>
                          ))}

                          <div className="meal-totals">
                            <span>{Math.round(mealTotals.calories)} cal</span>
                            <span>P: {Math.round(mealTotals.protein)}g</span>
                            <span>C: {Math.round(mealTotals.carbs)}g</span>
                            <span>F: {Math.round(mealTotals.fat)}g</span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MealPlanner;
